import { useEffect, useMemo, useState } from "react";
import { Medal } from "lucide-react";
import { adminApi } from "@/api/admin";
import { AgencyMultiSelect } from "@/components/admin/AgencyMultiSelect";
import { DateRangePicker } from "@/components/DateRangePicker";
import { KpiCard, RankBar, TrendLine, TrendMultiLine } from "@/components/charts";
import { formatMonth } from "@/lib/dates";
import { defaultRange, type Range } from "@/lib/periodo";
import type { CommissioniCross, OperatoriOut, PipelineOut, SerieOut, MargineOut } from "@/types/admin";

const eur = (n: number) => "€" + n.toLocaleString("it-IT", { minimumFractionDigits: 2 });

export function CommissioniCrossAgenzia() {
  const [range, setRange] = useState<Range>(useMemo(defaultRange, []));
  const [sel, setSel] = useState<{ id: string; name: string }[]>([]);
  const [cross, setCross] = useState<CommissioniCross | null>(null);
  const [serie, setSerie] = useState<SerieOut | null>(null);
  const [ops, setOps] = useState<OperatoriOut | null>(null);
  const [pipe, setPipe] = useState<PipelineOut | null>(null);
  const [margine, setMargine] = useState<MargineOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ids = sel.map((s) => s.id);
  const single = sel.length === 1;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([
      adminApi.commissioni(range.from, range.to, ids),
      adminApi.commissioniSerie(range.from, range.to, ids),
      adminApi.commissioniOperatori(range.from, range.to, ids),
      adminApi.commissioniPipeline(range.from, range.to, ids),
      adminApi.margine(range.from, range.to, ids),
    ]).then(([c, s, o, p, m]) => {
      if (cancelled) return;
      setCross(c); setSerie(s); setOps(o); setPipe(p); setMargine(m);
    }).catch(() => { if (!cancelled) setError("Impossibile caricare le commissioni."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, sel]);

  const totale = cross?.totale_complessivo ?? 0;
  const nPratiche = cross?.rows.reduce((s, r) => s + r.num_pratiche, 0) ?? 0;
  const sparkTot = serie?.aggregato.map((p) => p.total) ?? [];
  const sparkN = serie?.aggregato.map((p) => p.count) ?? [];
  const multi = sel.length >= 2 && (serie?.per_agenzia.length ?? 0) >= 2;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Commissioni — Cross agenzia</h2>
          <p className="text-sm text-muted-foreground">
            Riepilogo e andamento per agenzia — per data di accettazione
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <DateRangePicker value={range} onChange={setRange} />
          <AgencyMultiSelect selected={sel} onChange={setSel} />
        </div>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p>
        : loading || !cross || !serie || !ops || !pipe || !margine ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-28 bg-muted/40 rounded-lg animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiCard label="Totale periodo" value={eur(totale)} spark={sparkTot} />
              <KpiCard label="N° pratiche" value={String(nPratiche)} spark={sparkN} />
              <KpiCard label={single ? "Operatori" : "Agenzie"} value={String(single ? ops.rows.length : cross.rows.length)} />
              <KpiCard label="Media / agenzia" value={eur(cross.rows.length ? totale / cross.rows.length : 0)} />
              <KpiCard label="Top" value={(single ? ops.rows[0]?.operatore_name : cross.rows[0]?.agenzia_name) ?? "—"} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <KpiCard
                label="Ricavo"
                value={eur(margine.ricavo)}
                spark={margine.serie.map((p) => p.ricavo)}
              />
              <KpiCard
                label="Margine"
                value={eur(margine.margine)}
                spark={margine.serie.map((p) => p.margine)}
              />
              <KpiCard
                label="Margine %"
                value={margine.margine_pct != null ? `${margine.margine_pct.toFixed(1)}%` : "—"}
              />
            </div>
            {margine.pratiche_con_ricavo < margine.pratiche_totali && (
              <p className="text-xs text-muted-foreground">
                Ricavo/margine su {margine.pratiche_con_ricavo} di {margine.pratiche_totali} pratiche (solo quelle con CTE valorizzata).
              </p>
            )}

            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">Gettoni in maturazione</span>
                <span className="text-xs text-muted-foreground">pratiche aperte non ancora chiuse</span>
              </div>
              <div className="flex items-end gap-8">
                <div><div className="text-2xl font-bold">{eur(pipe.maturato)}</div><div className="text-xs text-muted-foreground">maturati</div></div>
                <div><div className="text-2xl font-bold text-blue-700">{pipe.pratiche_pipeline}</div><div className="text-xs text-muted-foreground">pratiche in pipeline</div></div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="text-sm font-semibold mb-3">{single ? `Commissioni per operatore — ${sel[0].name}` : "Ranking agenzie"}</div>
                <RankBar data={(single ? ops.rows.map((o) => ({ label: o.operatore_name ?? "—", value: o.totale }))
                  : cross.rows.map((r) => ({ label: r.agenzia_name ?? "—", value: r.totale })))} />
              </div>
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="text-sm font-semibold mb-3">
                  Trend — {range.from === range.to ? `${formatMonth(range.from)} (per giorno)` : `${range.from} → ${range.to}`}
                </div>
                {multi ? <TrendMultiLine series={serie.per_agenzia} /> : <TrendLine data={serie.aggregato} />}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="text-sm font-semibold mb-3">{single ? "N° pratiche per operatore" : "N° pratiche per agenzia"}</div>
                <RankBar currency={false} data={(single ? ops.rows.map((o) => ({ label: o.operatore_name ?? "—", value: o.num_pratiche }))
                  : cross.rows.map((r) => ({ label: r.agenzia_name ?? "—", value: r.num_pratiche })))} />
              </div>
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="text-sm font-semibold mb-3">Top operatori (commissioni)</div>
                <RankBar data={ops.rows.slice(0, 8).map((o) => ({ label: o.operatore_name ?? "—", value: o.totale }))} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <span className="text-sm font-semibold">{single ? `Operatori di ${sel[0].name}` : "Classifica operatori"}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  pratiche accettate nel periodo (l'attivazione può arrivare dopo)
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground"><tr>
                  <th className="text-left py-2 px-4 font-medium w-10">#</th>
                  <th className="text-left py-2 px-4 font-medium">Operatore</th>
                  <th className="text-left py-2 px-4 font-medium">Agenzia</th>
                  <th className="text-right py-2 px-4 font-medium">N° pratiche</th>
                  <th className="text-right py-2 px-4 font-medium">Totale</th>
                </tr></thead>
                <tbody>
                  {ops.rows.map((o, i) => (
                    <tr key={(o.operatore_id ?? "") + i} className="border-t border-border">
                      <td className="py-2 px-4 text-center">
                        {i === 0 ? <Medal size={16} className="text-yellow-500 inline" />
                          : i === 1 ? <Medal size={16} className="text-slate-400 inline" />
                          : i === 2 ? <Medal size={16} className="text-amber-700 inline" />
                          : i + 1}
                      </td>
                      <td className="py-2 px-4 font-medium">{o.operatore_name ?? "—"}</td>
                      <td className="py-2 px-4 text-muted-foreground">{o.agenzia_name ?? "—"}</td>
                      <td className="py-2 px-4 text-right">{o.num_pratiche}</td>
                      <td className="py-2 px-4 text-right font-medium">{eur(o.totale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!single && (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border text-sm font-semibold">Dettaglio per agenzia</div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-muted-foreground"><tr>
                    <th className="text-left py-2 px-4 font-medium">Agenzia</th>
                    <th className="text-right py-2 px-4 font-medium">N° pratiche</th>
                    <th className="text-right py-2 px-4 font-medium">Totale</th>
                    <th className="text-right py-2 px-4 font-medium">Δ vs prec.</th>
                  </tr></thead>
                  <tbody>
                    {cross.rows.map((r) => (
                      <tr key={r.agenzia_id ?? r.agenzia_name} onClick={() => r.agenzia_id && setSel([{ id: r.agenzia_id, name: r.agenzia_name ?? "" }])}
                        className="border-t border-border cursor-pointer hover:bg-surface">
                        <td className="py-2 px-4">{r.agenzia_name ?? "—"}</td>
                        <td className="py-2 px-4 text-right">{r.num_pratiche}</td>
                        <td className="py-2 px-4 text-right font-medium">{eur(r.totale)}</td>
                        <td className={`py-2 px-4 text-right ${r.delta_pct == null ? "text-muted-foreground" : r.delta_pct >= 0 ? "text-green-700" : "text-red-600"}`}>
                          {r.delta_pct == null ? "—" : `${r.delta_pct >= 0 ? "▲" : "▼"} ${Math.abs(r.delta_pct)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
    </div>
  );
}
